import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, Heart, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCopy } from '../context/SiteCopyContext';
import { useTaxonomy, slugify } from '../hooks/useTaxonomy';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import SearchPanel from './SearchPanel';
import NavFlyouts from './NavFlyouts';

const Header = () => {
  const { count, setIsOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { main, sub } = useTaxonomy();
  const location = useLocation();

  // Transparent mode: on the homepage at the very top, the header sits *over*
  // the hero video — no background, light text. As the user scrolls past the
  // hero (or navigates to any other route) it snaps back to the solid cream
  // bar so links stay readable on light content.
  const onHome = location.pathname === '/';
  const transparent = onHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = () => {
    /* Search now handled by SearchPanel component */
  };

  // Build dynamic mobile nav list: Home, Shop All, then 4 mains with expandable sub-lists, Journal, Contact.
  const [mobileExpandedMain, setMobileExpandedMain] = useState(null);

  const banner1 = useCopy('topbar.msg_1', 'Free Australia-wide delivery');
  const banner2 = useCopy('topbar.msg_2', '30-day returns');
  const banner3 = useCopy('topbar.msg_3', '2-year warranty');
  const banner4 = useCopy('topbar.msg_4', 'Australian made furniture');

  // Color tokens swap based on mode
  const headerWrapClass = onHome
    ? 'fixed top-0 left-0 right-0 z-40'
    : 'sticky top-0 z-40';
  // Marquee always stays solid brown — branding info needs to be readable
  // and consistent, not embedded in the hero video.
  const marqueeClass = 'bg-[#1E140B] text-[#E3D5B8] text-[11px] tracking-[0.22em] uppercase overflow-hidden border-b border-[#33200F]';
  const navWrapClass = transparent
    ? 'transition-all duration-300'
    : `transition-all duration-300 ${scrolled || !onHome ? 'bg-[#F5EDDD]/95 backdrop-blur-sm shadow-[0_1px_0_rgba(91,58,36,0.08)]' : 'bg-[#F5EDDD]'}`;
  const linkColor = transparent ? 'text-[#F5EDDD]' : 'text-[#33200F]';
  const accentColor = transparent ? 'text-[#F5EDDD]/85' : 'text-[#A07A4F]';

  return (
    <header className={headerWrapClass}>
      {/* Announcement marquee */}
      <div className={marqueeClass}>
        <div className="flex whitespace-nowrap py-2.5 marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0">
              {[banner1, banner2, banner3, banner4].filter(Boolean).map(
                (m, j) => (
                  <span key={j} className="px-8 inline-flex items-center gap-8">
                    <span>{m}</span>
                    <span className="text-[#A07A4F]">⁂</span>
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main nav */}
      <div className={navWrapClass}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-[78px] flex items-center justify-between">
          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open menu" className={`p-1.5 ${linkColor}`}>
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#F5EDDD] border-r border-[#C9B596]">
                <SheetHeader>
                  <SheetTitle
                    className="text-[#33200F] text-2xl text-left"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Timber <span className="italic text-[#A07A4F]">&amp;</span> Thread <span className="italic text-[#A07A4F]">co.</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 flex flex-col" data-testid="mobile-nav">
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="py-4 text-[#33200F] hover:text-[#A07A4F] text-lg tracking-wide border-b border-[#E3D5B8]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Home
                  </Link>
                  <Link
                    to="/collections/all"
                    onClick={() => setMobileOpen(false)}
                    className="py-4 text-[#33200F] hover:text-[#A07A4F] text-lg tracking-wide border-b border-[#E3D5B8]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Shop All
                  </Link>
                  {main.map((m) => {
                    const slugMain = slugify(m);
                    const expanded = mobileExpandedMain === m;
                    const subs = sub[m] || [];
                    return (
                      <div key={m} className="border-b border-[#E3D5B8]">
                        <div className="flex items-center justify-between">
                          <Link
                            to={`/collections/${slugMain}`}
                            onClick={() => setMobileOpen(false)}
                            data-testid={`mobile-nav-${slugMain}`}
                            className="flex-1 py-4 text-[#33200F] hover:text-[#A07A4F] text-lg tracking-wide"
                            style={{ fontFamily: "'Cormorant Garamond', serif" }}
                          >
                            {m}
                          </Link>
                          {subs.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setMobileExpandedMain(expanded ? null : m)}
                              aria-label={`Toggle ${m} sub-collections`}
                              className="p-2 text-[#A07A4F]"
                            >
                              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} strokeWidth={1.6} />
                            </button>
                          )}
                        </div>
                        {expanded && subs.length > 0 && (
                          <ul className="pb-3 pl-3">
                            {subs.map((s) => (
                              <li key={s}>
                                <Link
                                  to={`/collections/${slugMain}/${slugify(s)}`}
                                  onClick={() => setMobileOpen(false)}
                                  className="block py-2 text-[14px] text-[#5B3A24] hover:text-[#33200F]"
                                >
                                  {s}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                  <Link
                    to="/about"
                    onClick={() => setMobileOpen(false)}
                    className="py-4 text-[#33200F] hover:text-[#A07A4F] text-lg tracking-wide border-b border-[#E3D5B8]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    onClick={() => setMobileOpen(false)}
                    className="py-4 text-[#33200F] hover:text-[#A07A4F] text-lg tracking-wide border-b border-[#E3D5B8]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Contact
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo */}
          <Link
            to="/"
            className={`${linkColor} text-[12px] lg:text-[13px] xl:text-[15px] tracking-[0.18em] xl:tracking-[0.22em] whitespace-nowrap leading-none uppercase shrink-0 transition-colors`}
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Timber <span className={`italic ${accentColor} tracking-normal normal-case`}>&amp;</span> Thread <span className={accentColor}>Co.</span>
          </Link>

          {/* Desktop nav (hover flyouts) */}
          <NavFlyouts tone={transparent ? 'light' : 'dark'} />

          {/* Icons */}
          <div className={`flex items-center gap-2 sm:gap-4 ${linkColor}`}>
            <button
              aria-label="Search"
              data-testid="header-search-btn"
              onClick={() => setSearchOpen((s) => !s)}
              className="p-2 hover:text-[#A07A4F] transition-colors"
            >
              <Search className="h-[18px] w-[18px]" strokeWidth={1.4} />
            </button>
            <Link
              to="/wishlist"
              aria-label="Wishlist"
              data-testid="header-wishlist-btn"
              className="p-2 relative hover:text-[#A07A4F] transition-colors"
            >
              <Heart className="h-[18px] w-[18px]" strokeWidth={1.4} />
              {wishCount > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-[#5B3A24] text-[#F5EDDD] text-[10px] font-medium rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1">
                  {wishCount}
                </span>
              )}
            </Link>
            <button
              aria-label="Cart"
              onClick={() => setIsOpen(true)}
              className="p-2 relative hover:text-[#A07A4F] transition-colors"
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-[#5B3A24] text-[#F5EDDD] text-[10px] font-medium rounded-full h-[18px] min-w-[18px] flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        <SearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} />
      </div>
    </header>
  );
};

export default Header;
